import { useSkipCells } from "@/hooks/useSkipCells/useSkipCells";

import { ColumnHeader } from "@/pages/EditSubjectPlan/types/types";

import { TableData } from "@/states/unitTableReducer/unitTableReducer";

import { getFormattedCellKey } from "@/utils/getFormattedCellKey/getFormattedCellKey";

// bug: when no end date is selected, but the cohort is the same as the original custom unit's cohort, everything bast the span of the cell is disabled even if it's free

export const useIsOptionDisabled = ({
  customUnit,
  formValues,
  tableData,
}: {
  customUnit?: {
    cohort: string;
    startOnColumn: string;
    endOnColumn: string;
  } | null;
  formValues: {
    cohort: string;
    startOnColumn: string;
    endOnColumn: string;
  };
  tableData: Pick<TableData, "cells" | "columns" | "rows">;
}) => {
  const orderedColumns = [...tableData.columns].sort(
    (a, b) => a.order - b.order
  );

  // Use the useSkipCells hook to determine which cells to skip rendering
  const { skipSpannedCells } = useSkipCells(tableData.cells, orderedColumns);

  /**
   * The custom unit only matters when it's present, and the current cohort matches the original custom unit's cohort.
   */
  const isCustomUnitImportant =
    customUnit && formValues.cohort === customUnit.cohort;

  // Generic check between two column indexes to see if the current column is part of the range
  // Does not check anything else!
  const isColPartOfColumnRange = ({
    currentColIndex,
    endColIndex,
    startColIndex,
  }: {
    currentColIndex: number;
    startColIndex: number;
    endColIndex: number;
  }) => {
    return (
      customUnit &&
      formValues.cohort === customUnit.cohort &&
      startColIndex <= currentColIndex &&
      currentColIndex <= endColIndex
    );
  };

  // Simply check if a cell is occupied either by being spanned or having a unit directly in it.
  // We don't decide if the custom cell is important here as the cohort may have changed and isn't relevant
  const isOccupiedCell = ({
    columnId,
    rowId,
  }: {
    columnId: string;
    rowId: string;
  }) => {
    const formattedCellKey = getFormattedCellKey({
      column: columnId,
      row: rowId,
    });
    return (
      skipSpannedCells.has(formattedCellKey) ||
      tableData.cells.some(
        (cell) => cell.column === columnId && cell.row === rowId
      )
    );
  };

  /**
   * Checks if there is a blocking cell between the current cell and a target cell.
   * A blocking cell is defined as any occupied cell that is not part of the current custom unit.
   */
  const canFindBlockingCell = ({
    currentColIndex,
    targetColIndex,
  }: {
    /**
     * The index of the current column being evaluated.
     */
    currentColIndex: number;
    /**
     * The index of the target column to check against.
     */
    targetColIndex: number;
  }) => {
    if (targetColIndex !== -1 && currentColIndex > targetColIndex) {
      if (currentColIndex > targetColIndex) {
        return tableData.cells.some((cell) => {
          const cellColIndex = orderedColumns.findIndex(
            (col) => col.id === cell.column
          );

          // Prevent blocking if the cell is part of the current custom unit being edited and the cohort matches
          if (
            isCustomUnitImportant &&
            isColPartOfColumnRange({
              currentColIndex: cellColIndex,
              endColIndex: orderedColumns.findIndex(
                (c) => c.id === customUnit.endOnColumn
              ),
              startColIndex: orderedColumns.findIndex(
                (c) => c.id === customUnit.startOnColumn
              ),
            })
          ) {
            return false;
          }

          return (
            cell.row === formValues.cohort &&
            cellColIndex > targetColIndex &&
            cellColIndex < currentColIndex
          );
        });
      }
    }
    return false;
  };

  // Determine if a column should be disabled in the start dropdown
  const isStartColumnDisabled = (column: ColumnHeader): boolean => {
    const currentColIndex = orderedColumns.findIndex((c) => c.id === column.id);

    const startCustomColIndex = customUnit
      ? orderedColumns.findIndex((c) => c.id === customUnit.startOnColumn)
      : -1;
    const endCustomColIndex = customUnit
      ? orderedColumns.findIndex((c) => c.id === customUnit.endOnColumn)
      : -1;

    // if no cohort is selected, disable all options
    if (!formValues.cohort) {
      return true;
    }

    // The start column of the current custom unit should always be enabled
    if (
      isCustomUnitImportant &&
      isColPartOfColumnRange({
        currentColIndex,
        endColIndex: endCustomColIndex,
        startColIndex: startCustomColIndex,
      })
    ) {
      return false;
    }

    // With all the custom cells enabled, we now can safely disable any occupied cells
    if (isOccupiedCell({ columnId: column.id, rowId: formValues.cohort })) {
      return true;
    }

    // Finally, if the cell is not part of the custom cell, or it's unoccupied, we do not disable it
    return false;
  };

  // Helper function to determine if a column should be disabled in the end dropdown
  const isEndOnColumnDisabled = (column: ColumnHeader): boolean => {
    const startColIndex = orderedColumns.findIndex(
      (c) => c.id === formValues.startOnColumn
    );

    const startCustomCellColIndex = customUnit
      ? orderedColumns.findIndex((c) => c.id === customUnit.startOnColumn)
      : -1;
    const endCustomCellColIndex = customUnit
      ? orderedColumns.findIndex((c) => c.id === customUnit.endOnColumn)
      : -1;

    const endColIndex = orderedColumns.findIndex(
      (c) => c.id === formValues.endOnColumn
    );

    const currentColIndex = orderedColumns.findIndex((c) => c.id === column.id);

    // should disable all options when no start date is selected, even if a cohort is selected
    if (!formValues.startOnColumn) {
      return true;
    }

    // if the current options are set to the original custom unit values, and the column is within those ranges, enable the column
    if (
      isCustomUnitImportant &&
      // Important! We only want to fire this if the starts match, otherwise the end date will enable
      formValues.startOnColumn === customUnit.startOnColumn &&
      (formValues.endOnColumn === customUnit.endOnColumn ||
        !formValues.endOnColumn) &&
      isColPartOfColumnRange({
        currentColIndex,
        endColIndex: endCustomCellColIndex,
        startColIndex: startCustomCellColIndex,
      })
    ) {
      return false;
    }

    // if the selected start value is different from the custom unit's original start value, and the current cell is within  the original custom unit, enable it
    // This happens when a user has selected a start date within the original custom unit's range, so we want to ensure the entire original custom unit is still enabled
    if (
      isCustomUnitImportant &&
      // if the start dates don't match
      formValues.startOnColumn !== customUnit.startOnColumn &&
      // the col is within the original custom unit range
      isColPartOfColumnRange({
        currentColIndex,
        endColIndex: endCustomCellColIndex,
        startColIndex: startCustomCellColIndex,
      }) &&
      // _and_ the col is within the new range too
      isColPartOfColumnRange({
        currentColIndex,
        endColIndex: endCustomCellColIndex,
        startColIndex,
      }) &&
      // _and_ the new start date and the current col do not have any blocking cells between them
      !canFindBlockingCell({
        currentColIndex,
        targetColIndex: startColIndex,
      })
    ) {
      return false;
    }

    // If a cell is skipped, it means it's occupied by a spanning unit, so cannot be selected, but we need to ignore the current custom unit's spanned cells
    if (isOccupiedCell({ columnId: column.id, rowId: formValues.cohort })) {
      return true;
    }

    // End date cannot ever be before start date.
    const isBefore =
      orderedColumns.findIndex((c) => c.id === column.id) <
      orderedColumns.findIndex((c) => c.id === formValues.startOnColumn);

    if (isBefore) {
      return true;
    }

    // if the col index is after the end column, only enable free cells between the end cell and the next occupied cell.
    const hasOccupiedCellBetweenEndAndCurrent = canFindBlockingCell({
      currentColIndex,
      targetColIndex: endColIndex,
    });

    if (hasOccupiedCellBetweenEndAndCurrent) {
      return true;
    }

    // if, since the start date, any occupied column site between the start and current column that is not the current custom unit, disable the current column
    const hasOccupiedCellBetweenStartAndCurrent = canFindBlockingCell({
      currentColIndex,
      targetColIndex: startColIndex,
    });

    if (hasOccupiedCellBetweenStartAndCurrent) {
      return true;
    }

    return false;
  };

  return {
    isStartColumnDisabled,
    isEndOnColumnDisabled,
  };
};
